import { useCallback } from 'react';
import { toast } from 'react-toastify';

import { useFetchWerklijnenOpId } from '@/components/search/hooks/useFetchWerklijnenOpId';
import useLogger from '@/hooks/useLogger';
import useClientState from '@/state/useClientState';
import { TimestampedWerklijnUpdate } from '@/types/sseEvent';

import { ApiResourceKey } from '../types/types';

/**
 * Samen met `useWerklijnenSubscription()` de belangrijkste hook binnen deze file.
 * Deze functie verwerkt een nieuw WerklijnEvent.
 *
 * Bijgewerkte werklijnen worden opgehaald uit de backend en vervolgens in de store bijgewerkt.
 * Verwijderde werklijnen worden uit alle subscriptions verwijderd, en vervolgens ook uit de store.
 *
 * @returns een object met daarin de functie `verwerkWerklijnEvent` waarmee een Werklijn-event wordt verwerkt in de store.
 */
const useVerwerkWerklijnEvent = () => {
  const updateWerklijnInStore = useClientState((state) => state.updateWerklijn);
  const verwijderWerklijnUitStore = useClientState((state) => state.verwijderWerklijn);
  const persisteerWerklijnEventInStore = useClientState((state) => state.persisteerWerklijnEvent);
  const huidigeWerklijnIds = useClientState((state) => state.huidigeWerklijnIds);
  const handleApiError = useClientState(
    (state) => (queryKey: ApiResourceKey, isError: boolean) =>
      isError ? state.showApiError(queryKey) : state.resetApiError(queryKey),
  );
  const { fetchEnkeleWerklijnOpId } = useFetchWerklijnenOpId();
  const log = useLogger();
  const werklijnBijwerken = (werklijnId: string) => {
    console.log('SSE: Bijgewerkte werklijn ontvangen:', werklijnId);
    log(`SSE: Bijgewerkte werklijn ontvangen:${werklijnId}`);
    (async () => {
      if (huidigeWerklijnIds().includes(werklijnId)) {
        // Werklijn zit in de huidige state dus moet worden bijgewerkt:
        try {
          const bijgewerkteWerklijn = await fetchEnkeleWerklijnOpId(werklijnId).catch((error) => {
            console.error(error);
            return null;
          });
          if (bijgewerkteWerklijn) {
            updateWerklijnInStore(bijgewerkteWerklijn);
            handleApiError('verversWerklijnen', false);
          } else {
            // Mislukt (bv. timeout of niet gevonden): de banner niet wissen, de periodieke refresh herstelt dit.
            console.warn('Geen werklijn teruggekregen van backend n.a.v. event');
            log('Geen werklijn teruggekregen van backend n.a.v. event');
          }
        } catch (error) {
          console.error(error);
          log('Fout tijdens verversen werklijn');
          toast.error('Fout tijdens verversen werklijnen');
          handleApiError('verversWerklijnen', true);
        }
      } else {
        // Werklijn zit _niet_ in de huidige state:
        log(`Werklijn-update voor id '${werklijnId}' maakt geen onderdeel uit van de state dus wordt genegeerd`);
      }
    })();
  };

  const verwerkWerklijnEvent = useCallback(
    (event: TimestampedWerklijnUpdate) => {
      switch (event.status) {
        case 'TOEGEVOEGD':
          // Wel logggen, dit type event wordt verder nog niet gebruikt
          console.log('SSE: Toegevoegde werklijn ontvangen:', event.werklijnId);
          log(`SSE: Toegevoegde werklijn ontvangen:${event.werklijnId}`);
          break;
        case 'BIJGEWERKT':
          werklijnBijwerken(event.werklijnId);
          break;
        case 'BIJGEWERKT_INREGELING':
        case 'VERWIJDERD_INREGELING':
          werklijnBijwerken(event.werklijnIdBijInregeling);
          break;
        case 'VERWIJDERD':
          console.log('SSE: Verwijderde werklijn ontvangen:', event.werklijnId);
          log(`SSE: Verwijderde werklijn ontvangen:${event.werklijnId}`);
          verwijderWerklijnUitStore(event.werklijnId);
          break;
        case 'DEFECT_BIJGEWERKT':
          werklijnBijwerken(event.werklijnId);
          break;
        case 'UITVOERINGSMELDING_BIJGEWERKT':
          werklijnBijwerken(event.werklijnId);
          break;
        case 'CONFLICT_BIJGEWERKT':
          werklijnBijwerken(event.werklijnId);
          break;
      }
      persisteerWerklijnEventInStore(event);
    },
    [
      fetchEnkeleWerklijnOpId,
      huidigeWerklijnIds,
      persisteerWerklijnEventInStore,
      updateWerklijnInStore,
      verwijderWerklijnUitStore,
    ],
  );

  return { verwerkWerklijnEvent };
};
export default useVerwerkWerklijnEvent;
